/**
 * Quadro de verificação lido por eixos (E78.F3.T04) — o componente compartilhado
 * pela análise preliminar e pela mesa do perito.
 *
 * O quadro anterior empilhava onze seções com o mesmo peso: no caso real
 * GT-2026-0005 o perito rolava sete linhas de assinatura embutida para ler
 * "não se aplica" antes de chegar ao único item divergente. Aqui os mesmos
 * dados ficam em seis eixos; a tira mostra, por eixo, quanta divergência há, e
 * o eixo divergente de menor ordem já vem aberto. Eixo em que tudo confere fica
 * só no contador — quem quiser lê, clicando.
 *
 * Nada é recalculado: a repartição e a contagem vêm de `exame_eletronico_eixos`,
 * e cada painel monta as MESMAS peças do quadro item a item.
 */
import { useMemo, useState } from 'react';

import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  eixoQueAbrePrimeiro,
  eixosDoQuadro,
  type ChaveEixo,
  type EixoDoQuadro,
} from '@/lib/exame_eletronico_eixos';
import type { ResultadoExameEletronicoData } from '@/lib/exame_eletronico';
import type { Tendencia } from '@/lib/pre_analise';

import {
  PainelAlertas,
  PainelIdentidade,
  PainelIntegridade,
  PainelLocalizacao,
  PainelRede,
  PainelTrilha,
  type PainelDoEixoProps,
} from './QuadroPorEixosPaineis';
import { formatarInstante, GrauETendencia } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.eixos;

const PAINEL: Record<ChaveEixo, (props: PainelDoEixoProps) => JSX.Element> = {
  integridade: PainelIntegridade,
  identidade: PainelIdentidade,
  trilha: PainelTrilha,
  rede: PainelRede,
  localizacao: PainelLocalizacao,
  alertas: PainelAlertas,
};

/**
 * O que o contador diz por extenso, para quem não vê a cor.
 *
 * Divergência é contradição apurada; sem nenhuma, o número passa a ser quantos
 * itens o eixo examinou — nunca um "0" mudo que se leria como "nada aqui".
 */
function rotuloDoContador(eixo: EixoDoQuadro): string {
  if (eixo.divergencias > 0) {
    const molde = eixo.divergencias === 1 ? t.contadorDivergencia : t.contadorDivergencias;
    return molde.replace('{n}', String(eixo.divergencias));
  }
  if (eixo.examinados === 0) return t.contadorVazio;
  const molde = eixo.examinados === 1 ? t.contadorConfere : t.contadorConferem;
  return molde.replace('{n}', String(eixo.examinados));
}

function Contador({ eixo }: { eixo: EixoDoQuadro }): JSX.Element {
  const divergente = eixo.divergencias > 0;
  return (
    <Badge variant={divergente ? 'danger' : 'neutral'}>
      {divergente ? eixo.divergencias : eixo.examinados}
    </Badge>
  );
}

export interface QuadroPorEixosProps {
  resultado: ResultadoExameEletronicoData;
  tendencia: Tendencia | null;
}

export function QuadroPorEixos({ resultado, tendencia }: QuadroPorEixosProps): JSX.Element {
  const eixos = useMemo(() => eixosDoQuadro(resultado), [resultado]);
  const padrao = eixoQueAbrePrimeiro(eixos);

  // Trocar de peça troca o quadro: o eixo aberto volta a ser o divergente da
  // peça NOVA, não o que o perito tinha aberto na anterior. Ajuste no próprio
  // render (padrão do React para "estado derivado de prop que mudou") em vez de
  // efeito — um efeito aqui renderizaria uma vez o eixo errado antes de corrigir.
  const [visto, setVisto] = useState(resultado);
  const [escolhido, setEscolhido] = useState<ChaveEixo | null>(null);
  if (visto !== resultado) {
    setVisto(resultado);
    setEscolhido(null);
  }
  const aberto = escolhido ?? padrao;

  return (
    <div className="space-y-sc-4" data-testid="quadro-por-eixos" data-eixo-aberto={aberto}>
      <GrauETendencia grau={resultado.grau_convergencia} tendencia={tendencia} />

      <Tabs value={aberto} onValueChange={(valor) => setEscolhido(valor as ChaveEixo)}>
        <TabsList className="flex w-full flex-wrap" data-testid="quadro-eixos-tira">
          {eixos.map((eixo) => (
            <TabsTrigger
              key={eixo.chave}
              value={eixo.chave}
              data-testid={`quadro-eixo-${eixo.chave}`}
              data-divergencias={eixo.divergencias}
              aria-label={`${t.nomes[eixo.chave]} — ${rotuloDoContador(eixo)}`}
            >
              {t.nomes[eixo.chave]}
              <Contador eixo={eixo} />
            </TabsTrigger>
          ))}
        </TabsList>
        {eixos.map((eixo) => {
          const Painel = PAINEL[eixo.chave];
          return (
            <TabsContent key={eixo.chave} value={eixo.chave} className="mt-sc-4">
              <Painel resultado={resultado} />
            </TabsContent>
          );
        })}
      </Tabs>

      <p
        className="border-t border-border pt-sc-2 text-caption text-fg-dim"
        data-testid="quadro-metodologia"
      >
        {t.metodologia}:{' '}
        {t.metodologiaLinha
          .replace('{versao}', resultado.parametros_versao)
          .replace('{quando}', formatarInstante(resultado.executado_em, false))}
      </p>
    </div>
  );
}
